import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  User,
  UserCredential,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup,
  FacebookAuthProvider,
  TwitterAuthProvider
} from 'firebase/auth';
import { auth } from '../lib/firebase';

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
}

class AuthService {
  // Sign up with email and password
  async signUp(email: string, password: string, displayName?: string): Promise<UserCredential> {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    if (displayName && userCredential.user) {
      await updateProfile(userCredential.user, { displayName });
    }

    // Send email verification
    if (userCredential.user) {
      await sendEmailVerification(userCredential.user);
    }

    return userCredential;
  }

  // Sign in with email and password
  async signIn(email: string, password: string): Promise<UserCredential> {
    return await signInWithEmailAndPassword(auth, email, password);
  }

  // Sign in with Google
  async signInWithGoogle(): Promise<UserCredential> {
    const provider = new GoogleAuthProvider();
    return await signInWithPopup(auth, provider);
  }

  // Sign in with Facebook
  async signInWithFacebook(): Promise<UserCredential> {
    const provider = new FacebookAuthProvider();
    return await signInWithPopup(auth, provider);
  }

  // Sign in with Twitter
  async signInWithTwitter(): Promise<UserCredential> {
    const provider = new TwitterAuthProvider();
    return await signInWithPopup(auth, provider);
  }

  // Sign out
  async signOut(): Promise<void> {
    return await signOut(auth);
  }

  // Reset password
  async resetPassword(email: string): Promise<void> {
    return await sendPasswordResetEmail(auth, email);
  }

  // Update user profile
  async updateUserProfile(updates: { displayName?: string; photoURL?: string }): Promise<void> {
    if (auth.currentUser) {
      return await updateProfile(auth.currentUser, updates);
    }
    throw new Error('No user is currently signed in');
  }

  // Get current user
  getCurrentUser(): User | null {
    return auth.currentUser;
  }

  // Get formatted user data
  getFormattedUser(user: User): AuthUser {
    return {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      emailVerified: user.emailVerified
    };
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!auth.currentUser;
  }

  // Send email verification
  async sendEmailVerification(): Promise<void> {
    if (auth.currentUser) {
      return await sendEmailVerification(auth.currentUser);
    }
    throw new Error('No user is currently signed in');
  }
}

export const authService = new AuthService();
export default authService;